import { useAppSelector } from "@/Hooks/redux/useAppSelector";
import { ICampaignItem, ICampaignItemChildren } from "@/Models/ApiModels";
import modalStyles from "@/Theme/modalsElements.module.scss";
import { Modal, Popover } from "@mui/material";
import React, { useState } from "react";
import { OrdersTd, OrdersTr } from "../../styled";
import ArchiveOrderModal from "../Modals/ArchiveOrderModal/ArchiveOrderModal";
import CancelDelayedOrderModal from "../Modals/CancelDelayedOrderModal";
import CancelOrderModal from "../Modals/CancelOrderModal";
import PromotePopover from "../Modals/PromotePopover";
import OrderTableButton from "../OrderTableButton";
import ProgressItem from "../ProgressItem";
import StatusItem from "../StatusItem";
import { AppNameBlock, AppNameIcon, AppNameText } from "./styled";

interface OrdersCampaignItemProps {
  orderInfo: ICampaignItem;
  fetchNewOrdersList: () => void;
  detailsHandle: (id: number | null, appName: string, appIcon: string) => void;
}

const OrdersCampaignItem: React.FC<OrdersCampaignItemProps> = ({
  orderInfo,
  fetchNewOrdersList,
  detailsHandle,
}) => {
  const {
    id,
    appName,
    appIcon,
    geo,
    type,
    count,
    days,
    price,
    startTime,
    status,
    children,
  } = orderInfo;

  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openArchiveModal, setOpenArchiveModal] = useState(false);
  const [openDelayedModal, setOpenDelayedModal] = useState(false);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const openPopover = Boolean(anchorEl);

  const selectedColumns = useAppSelector((state) => state.myOrdersColumnsSlice);

  const handleToggleCancelModal = () => {
    setOpenCancelModal(!openCancelModal);
  };

  const handleToggleArchiveModal = () => {
    setOpenArchiveModal(!openArchiveModal);
  };

  const handleOpenDelayedModal = () => {
    setOpenDelayedModal(true);
  };

  const handleCloseDelayedModal = () => {
    setOpenDelayedModal(false);
  };

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const getGeoString = (geo: string) => {
    if (!geo) return;

    const geoList = geo.split(",");

    if (geoList.length > 3) {
      return `${geoList.splice(0, 3).join(",")}...`;
    } else {
      return geoList.join(",");
    }
  };

  const getCorrectTypeTitle = (
    type: string,
    children: ICampaignItemChildren[],
  ): React.ReactNode => {
    switch (type) {
      case "install":
        return "Install";
      case "rate":
        return "Rate";
      case "review":
        return "Review AI";
      case "review-manual":
        return "Review";
      case "install_bp":
        return "Package";
      case "install_with_views":
        return "Install";
      default:
        return type;
    }
  };

  interface IRowCell {
    key: keyof ICampaignItem | "startDate" | "progress" | "app" | "impressions";
    element?: React.ReactNode;
    title?: string;
  }

  const ROW_CELLS: IRowCell[] = [
    {
      key: "id",
    },
    {
      key: "app",
      element: (
        <AppNameBlock>
          <AppNameIcon src={appIcon} />
          <AppNameText>{appName}</AppNameText>
        </AppNameBlock>
      ),
      title: appName,
    },
    {
      key: "geo",
      element: getGeoString(geo),
      title: geo,
    },
    { key: "type", element: getCorrectTypeTitle(type, children) },
    {
      key: "count",
    },
    {
      key: "impressions",
      element: orderInfo.children[0]?.count ?? "—",
    },
    {
      key: "days",
    },
    {
      key: "price",
      element: `$ ${price.toFixed(2)}`,
    },
    { key: "startDate", element: startTime },
    { key: "progress", element: <ProgressItem days={days} status={status} /> },
    { key: "status", element: <StatusItem status={status} /> },
  ];

  return (
    <>
      <OrdersTr>
        {ROW_CELLS.map((cell, i) =>
          selectedColumns[cell.key] ? (
            <OrdersTd
              title={cell.title ? cell.title : ""}
              onClick={() => detailsHandle(id, appName, appIcon)}
              key={i}
            >
              {cell.key === "startDate" ||
              cell.key === "progress" ||
              cell.key === "app" ||
              cell.key === "children" ||
              cell.key === "impressions"
                ? cell.element
                : (cell.element ?? orderInfo[cell.key])}
            </OrdersTd>
          ) : null,
        )}
        <OrdersTd>
          <OrderTableButton
            role={"cancel"}
            onClick={handleToggleCancelModal}
            disabled={!(status === 1 || status === 2 || status === 7)}
            title={"Stop the order"}
          />
        </OrdersTd>
        <OrdersTd>
          <OrderTableButton
            role={"promote"}
            onClick={handleOpenPopover}
            title={"Duplicate or extend the campaign"}
            disabled={appName?.includes("banned")}
          />
        </OrdersTd>
        <OrdersTd>
          <OrderTableButton
            role={"archive"}
            onClick={handleToggleArchiveModal}
            disabled={
              status === 1 || status === 2 || status === 6 || status === 7
            }
            title={"Archive the campaign"}
          />
        </OrdersTd>
      </OrdersTr>
      <Modal
        open={openCancelModal}
        onClose={handleToggleCancelModal}
        className={modalStyles.modal}
      >
        <CancelOrderModal
          orderInfo={orderInfo}
          onClose={handleToggleCancelModal}
          fetchNewOrdersList={fetchNewOrdersList}
          openDelayedAttention={handleOpenDelayedModal}
        />
      </Modal>
      <Modal
        open={openDelayedModal}
        onClose={handleCloseDelayedModal}
        className={modalStyles.modal}
      >
        <CancelDelayedOrderModal onClose={handleCloseDelayedModal} />
      </Modal>
      <Modal
        open={openArchiveModal}
        onClose={handleToggleArchiveModal}
        className={modalStyles.modal}
      >
        <ArchiveOrderModal
          orderInfo={orderInfo}
          onClose={handleToggleArchiveModal}
          fetchNewOrdersList={fetchNewOrdersList}
        />
      </Modal>
      <Popover
        style={{ boxShadow: "unset", borderRadius: "12px" }}
        open={openPopover}
        onClose={handleClosePopover}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <PromotePopover campaignId={id} />
      </Popover>
    </>
  );
};

export default OrdersCampaignItem;
